import React from 'react'
import { MapPin, Calendar, DollarSign, Bookmark, ExternalLink } from 'lucide-react'

const GrantCard = ({ grant, isSaved, onSave }) => {
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const getTypeColor = (type) => {
    const colors = {
      climate: 'bg-green-100 text-green-700',
      education: 'bg-blue-100 text-blue-700',
      research: 'bg-purple-100 text-purple-700',
      arts: 'bg-pink-100 text-pink-700',
      business: 'bg-orange-100 text-orange-700',
      'sustainable-development': 'bg-teal-100 text-teal-700',
    }
    return colors[type] || 'bg-gray-100 text-gray-700'
  }

  return (
    <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-6 flex flex-col" data-component-path="client/src/components/GrantCard.jsx" data-component-line="28:5" data-component-id="client/src/components/GrantCard.jsx:28:5">
      <div className="flex items-start justify-between mb-4" data-component-path="client/src/components/GrantCard.jsx" data-component-line="29:7" data-component-id="client/src/components/GrantCard.jsx:29:7">
        <div className="flex-1" data-component-path="client/src/components/GrantCard.jsx" data-component-line="30:9" data-component-id="client/src/components/GrantCard.jsx:30:9">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getTypeColor(grant.type)}`} data-component-path="client/src/components/GrantCard.jsx" data-component-line="31:11" data-component-id="client/src/components/GrantCard.jsx:31:11">
            {grant.type.charAt(0).toUpperCase() + grant.type.slice(1).replace('-', ' ')}
          </span>
        </div>
        <button
          onClick={() => onSave(grant.id)}
          className={`p-2 rounded-lg transition-colors ${
            isSaved ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
          }`}
          data-component-path="client/src/components/GrantCard.jsx" data-component-line="35:9" data-component-id="client/src/components/GrantCard.jsx:35:9"
        >
          <Bookmark className={`w-5 h-5 ${isSaved ? 'fill-current' : ''}`} data-component-path="client/src/components/GrantCard.jsx" data-component-line="41:11" data-component-id="client/src/components/GrantCard.jsx:41:11" />
        </button>
      </div>

      <h3 className="text-xl font-bold text-gray-900 mb-2" data-component-path="client/src/components/GrantCard.jsx" data-component-line="45:7" data-component-id="client/src/components/GrantCard.jsx:45:7">{grant.title}</h3>
      <p className="text-gray-600 text-sm mb-4" data-component-path="client/src/components/GrantCard.jsx" data-component-line="46:7" data-component-id="client/src/components/GrantCard.jsx:46:7">{grant.organization}</p>
      <p className="text-gray-700 mb-4 flex-grow" data-component-path="client/src/components/GrantCard.jsx" data-component-line="47:7" data-component-id="client/src/components/GrantCard.jsx:47:7">{grant.description}</p>

      <div className="space-y-2 mb-4" data-component-path="client/src/components/GrantCard.jsx" data-component-line="49:7" data-component-id="client/src/components/GrantCard.jsx:49:7">
        <div className="flex items-center gap-2 text-gray-600" data-component-path="client/src/components/GrantCard.jsx" data-component-line="50:9" data-component-id="client/src/components/GrantCard.jsx:50:9">
          <DollarSign className="w-4 h-4 flex-shrink-0" data-component-path="client/src/components/GrantCard.jsx" data-component-line="51:11" data-component-id="client/src/components/GrantCard.jsx:51:11" />
          <span className="text-sm font-semibold text-green-600" data-component-path="client/src/components/GrantCard.jsx" data-component-line="52:11" data-component-id="client/src/components/GrantCard.jsx:52:11">{formatAmount(grant.amount)}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-600" data-component-path="client/src/components/GrantCard.jsx" data-component-line="54:9" data-component-id="client/src/components/GrantCard.jsx:54:9">
          <MapPin className="w-4 h-4 flex-shrink-0" data-component-path="client/src/components/GrantCard.jsx" data-component-line="55:11" data-component-id="client/src/components/GrantCard.jsx:55:11" />
          <span className="text-sm" data-component-path="client/src/components/GrantCard.jsx" data-component-line="56:11" data-component-id="client/src/components/GrantCard.jsx:56:11">{grant.location}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-600" data-component-path="client/src/components/GrantCard.jsx" data-component-line="58:9" data-component-id="client/src/components/GrantCard.jsx:58:9">
          <Calendar className="w-4 h-4 flex-shrink-0" data-component-path="client/src/components/GrantCard.jsx" data-component-line="59:11" data-component-id="client/src/components/GrantCard.jsx:59:11" />
          <span className="text-sm" data-component-path="client/src/components/GrantCard.jsx" data-component-line="60:11" data-component-id="client/src/components/GrantCard.jsx:60:11">Deadline: {grant.deadline}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-component-path="client/src/components/GrantCard.jsx" data-component-line="64:7" data-component-id="client/src/components/GrantCard.jsx:64:7">
        {grant.targetAudience.slice(0, 3).map((audience, index) => (
          <span
            key={index}
            className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs"
            data-component-path="client/src/components/GrantCard.jsx" data-component-line="66:11" data-component-id="client/src/components/GrantCard.jsx:66:11"
          >
            {audience}
          </span>
        ))}
      </div>

      <button className="w-full flex items-center justify-center gap-2 h-11 px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-component-path="client/src/components/GrantCard.jsx" data-component-line="74:7" data-component-id="client/src/components/GrantCard.jsx:74:7">
        <span data-component-path="client/src/components/GrantCard.jsx" data-component-line="75:9" data-component-id="client/src/components/GrantCard.jsx:75:9">View Details</span>
        <ExternalLink className="w-4 h-4" data-component-path="client/src/components/GrantCard.jsx" data-component-line="76:9" data-component-id="client/src/components/GrantCard.jsx:76:9" />
      </button>
    </div>
  )
}

export default GrantCard